import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { GA_MEASUREMENT_ID, useAnalytics } from "@/lib/analytics";
import { SessionProvider } from "@/hooks/use-session";
import { AuthProvider } from "@/hooks/use-auth";
import { PresenceProvider } from "@/hooks/use-presence";
import { EntitlementProvider } from "@/hooks/use-entitlement";
import { PremiumLockProvider } from "@/components/billing/premium-lock";
import { AuthGateProvider } from "@/components/auth/auth-gate";
import { ThemeProvider } from "@/hooks/use-theme";
import { AppShell } from "@/components/app/app-shell";

// Promotional overlays are not part of the first paint.
const IndependenceOffer = lazy(() =>
  import("@/components/app/independence-offer").then((m) => ({ default: m.IndependenceOffer })),
);
const SubscriptionReminder = lazy(() =>
  import("@/components/billing/subscription-reminder").then((m) => ({
    default: m.SubscriptionReminder,
  })),
);
import { HomeSeoContent } from "@/components/app/home-seo-content";
import { OG_IMAGE, SITE_NAME, SITE_URL } from "@/lib/seo";
import { registerPwa } from "@/lib/pwa";

const FONT_CSS_HREF =
  "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Cormorant+Garamond:ital,wght@1,500&family=Space+Grotesk:wght@500;700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&display=swap";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "theme-color", content: "#000000" },
      {
        name: "google-site-verification",
        content: "he6ZJvMR_uyysDMM0UEFvWg9fUSXX-NceHvMbQGWNCQ",
      },
      { title: "The Quantum Minds — JEE & NEET Study Tracker" },
      {
        name: "description",
        content:
          "Smart revision planner, question tracker, interactive simulations and virtual physics labs for JEE & NEET aspirants.",
      },
      { name: "author", content: SITE_NAME },
      { name: "application-name", content: SITE_NAME },
      { name: "apple-mobile-web-app-title", content: SITE_NAME },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "msapplication-TileColor", content: "#000000" },
      { name: "msapplication-config", content: "/browserconfig.xml" },
      { name: "format-detection", content: "telephone=no" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Warm the TLS handshake for the backend + analytics before first use.
      ...(import.meta.env.VITE_SUPABASE_URL
        ? [
            {
              rel: "preconnect",
              href: import.meta.env.VITE_SUPABASE_URL as string,
              crossOrigin: "anonymous" as const,
            },
          ]
        : []),
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/icon-32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/icon-16.png", type: "image/png", sizes: "16x16" },
      { rel: "apple-touch-icon", href: "/icon-180.png", sizes: "180x180" },
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
    scripts: [
      // Apply the persisted theme palette / mode before first paint.
      {
        children: `(function(){try{var d=document.documentElement;var p=localStorage.getItem('quantum_minds_theme')||'background';d.setAttribute('data-theme',p);var sys=localStorage.getItem('quantum_minds_theme_system')==='1';var m=localStorage.getItem('stp_theme');var dark=p==='sand'?false:((sys?window.matchMedia('(prefers-color-scheme: dark)').matches:(m?m==='dark':true)));d.classList.toggle('dark',dark);}catch(e){}})();`,
      },
      // Web fonts load without blocking the first paint (display=swap keeps text
      // visible in the fallback face until they arrive).
      {
        children: `(function(){var l=document.createElement('link');l.rel='stylesheet';l.href=${JSON.stringify(FONT_CSS_HREF)};l.media='print';l.onload=function(){this.media='all'};document.head.appendChild(l);})();`,
      },

      // Analytics queue exists immediately; the 40 KB loader waits for idle time
      // after the page has painted, together with the ad script.
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}',{send_page_view:false});(function(){var done=false;function add(src,attrs){var s=document.createElement('script');s.src=src;s.async=true;if(attrs)for(var k in attrs)s.setAttribute(k,attrs[k]);document.head.appendChild(s);}function load(){if(done)return;done=true;add('https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}');add('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1008632903948995',{crossorigin:'anonymous'});}function schedule(){(window.requestIdleCallback||function(f){setTimeout(f,2000)})(load,{timeout:5000});}if(document.readyState==='complete'){schedule();}else{window.addEventListener('load',schedule,{once:true});}})();`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: SITE_NAME,
              url: SITE_URL,
              logo: `${SITE_URL}/icon-512.png`,
              email: "thequantummindsofficial@gmail.com",
            },
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              name: SITE_NAME,
              url: SITE_URL,
              inLanguage: "en",
              publisher: { "@id": `${SITE_URL}/#organization` },
            },
            {
              "@type": "WebApplication",
              "@id": `${SITE_URL}/#webapp`,
              name: SITE_NAME,
              url: SITE_URL,
              applicationCategory: "EducationalApplication",
              operatingSystem: "Web, Android, iOS",
              image: OG_IMAGE,
              description:
                "Smart revision planner, question tracker, interactive simulations and virtual physics labs for JEE & NEET aspirants.",
              publisher: { "@id": `${SITE_URL}/#organization` },
              offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" className="dark" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="bg-background text-foreground antialiased">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useAnalytics();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [deferredReady, setDeferredReady] = useState(false);

  useEffect(() => {
    void registerPwa();
    const ric = (window as Window & typeof globalThis).requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(() => setDeferredReady(true), { timeout: 4000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setDeferredReady(true), 1500);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {pathname === "/" ? <HomeSeoContent /> : null}
      <ThemeProvider>
        <AuthProvider>
          <PresenceProvider>
           <EntitlementProvider>
            <PremiumLockProvider>
              <AuthGateProvider>
                <SessionProvider>
                  <AppShell>
                    {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
                    <Outlet />
                  </AppShell>
                  {deferredReady ? (
                    <Suspense fallback={null}>
                      <IndependenceOffer />
                      <SubscriptionReminder />
                    </Suspense>
                  ) : null}
                </SessionProvider>
              </AuthGateProvider>
            </PremiumLockProvider>
           </EntitlementProvider>
          </PresenceProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
